import {
	BankOutlined,
	BellOutlined,
	CheckCircleOutlined,
	DashboardOutlined,
	DownOutlined,
	EnvironmentOutlined,
	EyeOutlined,
	FileOutlined,
	FileProtectOutlined,
	FolderOutlined,
	InboxOutlined,
	LogoutOutlined,
	PlayCircleOutlined,
	PlusCircleOutlined,
	PlusOutlined,
	SettingOutlined,
	SyncOutlined,
	TeamOutlined,
	ThunderboltOutlined,
	ToolOutlined,
	UnorderedListOutlined,
	UserOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { type Dispatch } from "@reduxjs/toolkit";
import { Avatar, Badge, Button, Dropdown, Flex, Layout, Menu, Tag, theme, Typography, type MenuProps } from "antd";
import type { ItemType } from "antd/es/menu/interface";
import React, { useMemo } from "react";
import { connect } from "react-redux";
import { actions } from "redux-router5";
import type { State } from "router5";
import { AppBreadCrumbs } from "../common/components/AppBreadCrumbs";
import { getUserDisplayName, getUserInitials } from "../common/utils/userDisplayName";
import { useNotifications } from "../common/utils/useNotifications";
import { managementNotificationsAdapter } from "./notifications/managementNotificationsAdapter";
import { NotificationBell } from "./notifications/NotificationBell";
import { routeNames } from "./common/routes";
import type { UserState } from "../features/user/userSlice";
import type { RouteState } from "../types/router";
import styles from "./AppLayout.module.css";
import type { RootState } from "./store";
import { routerInstance } from "./store";

const { Header, Content, Footer, Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

const TICKET_ALL_KEY = "tickets.list.all";
const ticketStatusKey = (status: string) => `tickets.list.status.${status}`;

const PROJECT_ALL_KEY = "projects.list.all";
const projectStatusKey = (status: string) => `projects.list.status.${status}`;

function getItem(
	label: React.ReactNode,
	key: React.Key,
	dispatch: Dispatch,
	dest?: string,
	children?: MenuItem[],
	icon?: React.ReactNode,
): MenuItem {
	if (!dest) {
		return {
			key,
			label,
			children,
			icon,
		} as MenuItem;
	}
	const routes = routerInstance.buildUrl(dest);
	return {
		key,
		label: (
			<a
				href={routes}
				onClick={(event) => {
					event.preventDefault();
					dispatch(actions.navigateTo(dest));
				}}
			>
				{label}
			</a>
		),
		children,
		icon,
	} as MenuItem;
}

function getNavItem(
	label: React.ReactNode,
	key: React.Key,
	dispatch: Dispatch,
	dest: string,
	params: Record<string, string>,
	icon?: React.ReactNode,
): MenuItem {
	const url = routerInstance.buildUrl(dest, params);
	return {
		key,
		label: (
			<a
				href={url}
				onClick={(event) => {
					event.preventDefault();
					dispatch(actions.navigateTo(dest, params));
				}}
			>
				{label}
			</a>
		),
		icon,
	} as MenuItem;
}

function getMenuItems(
	intl: IntlShape,
	dispatch: Dispatch,
	unreadNotificationCount: number,
) {
	return [
		getItem(
			<Flex align="center" gap={8}>
				<div className={styles.brandIcon}>
					<ThunderboltOutlined />
				</div>
				<span className={styles.brandName}>
					{intl.formatMessage({ id: "menu_header_system_name" })}
				</span>
			</Flex>,
			"logo",
			dispatch,
		),
		{ type: "divider" } as ItemType,
		getItem(
			intl.formatMessage({ id: "nav_dashboard" }),
			routeNames.DASHBOARD,
			dispatch,
			routeNames.DASHBOARD,
			undefined,
			<DashboardOutlined />,
		),
		{ type: "divider" } as ItemType,
		getItem(
			intl.formatMessage({ id: "nav_group_projects" }),
			routeNames.PROJECTS,
			dispatch,
			undefined,
			[
				getNavItem(
					intl.formatMessage({ id: "nav_projects_all" }),
					PROJECT_ALL_KEY,
					dispatch,
					routeNames.PROJECT_LIST,
					{},
					<UnorderedListOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_projects_active" }),
					projectStatusKey("ACTIVE"),
					dispatch,
					routeNames.PROJECT_LIST,
					{ project_status: "ACTIVE" },
					<PlayCircleOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_projects_completed" }),
					projectStatusKey("COMPLETED"),
					dispatch,
					routeNames.PROJECT_LIST,
					{ project_status: "COMPLETED" },
					<CheckCircleOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_projects_delayed" }),
					projectStatusKey("DELAYED"),
					dispatch,
					routeNames.PROJECT_LIST,
					{ project_status: "DELAYED" },
					<WarningOutlined />,
				),
				getItem(
					intl.formatMessage({ id: "menu_side_create_project" }),
					routeNames.PROJECT_CREATE,
					dispatch,
					routeNames.PROJECT_CREATE,
					undefined,
					<PlusCircleOutlined />,
				),
			],
			<FolderOutlined />,
		),
		getItem(
			intl.formatMessage({ id: "nav_group_work_orders" }),
			routeNames.TICKETS,
			dispatch,
			undefined,
			[
				getNavItem(
					intl.formatMessage({ id: "nav_work_orders_all" }),
					TICKET_ALL_KEY,
					dispatch,
					routeNames.TICKET_LIST,
					{},
					<UnorderedListOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_work_orders_new" }),
					ticketStatusKey("NEW"),
					dispatch,
					routeNames.TICKET_LIST,
					{ status: "NEW" },
					<FileOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_work_orders_in_progress" }),
					ticketStatusKey("IN_PROGRESS"),
					dispatch,
					routeNames.TICKET_LIST,
					{ status: "IN_PROGRESS" },
					<SyncOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_work_orders_standby" }),
					ticketStatusKey("STANDBY"),
					dispatch,
					routeNames.TICKET_LIST,
					{ status: "STANDBY" },
					<WarningOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_work_orders_in_review" }),
					ticketStatusKey("IN_REVIEW"),
					dispatch,
					routeNames.TICKET_LIST,
					{ status: "IN_REVIEW" },
					<EyeOutlined />,
				),
				getNavItem(
					intl.formatMessage({ id: "nav_work_orders_done" }),
					ticketStatusKey("DONE"),
					dispatch,
					routeNames.TICKET_LIST,
					{ status: "DONE" },
					<CheckCircleOutlined />,
				),
				getItem(
					intl.formatMessage({ id: "menu_side_create_ticket" }),
					routeNames.TICKET_CREATE,
					dispatch,
					routeNames.TICKET_CREATE,
					undefined,
					<PlusCircleOutlined />,
				),
			],
			<FileProtectOutlined />,
		),
		getItem(
			intl.formatMessage({ id: "nav_group_clients" }),
			routeNames.CLIENTS,
			dispatch,
			undefined,
			[
				getItem(
					intl.formatMessage({ id: "menu_side_clients" }),
					routeNames.CLIENT_LIST,
					dispatch,
					routeNames.CLIENT_LIST,
					undefined,
					<UnorderedListOutlined />,
				),
				getItem(
					intl.formatMessage({ id: "menu_side_create_client" }),
					routeNames.CLIENT_CREATE,
					dispatch,
					routeNames.CLIENT_CREATE,
					undefined,
					<PlusCircleOutlined />,
				),
			],
			<BankOutlined />,
		),
		getItem(
			intl.formatMessage({ id: "nav_group_turbines" }),
			routeNames.TURBINES,
			dispatch,
			undefined,
			[
				getItem(
					intl.formatMessage({ id: "menu_side_turbines" }),
					routeNames.TURBINE_LIST,
					dispatch,
					routeNames.TURBINE_LIST,
					undefined,
					<UnorderedListOutlined />,
				),
				getItem(
					intl.formatMessage({ id: "menu_side_create_turbines" }),
					routeNames.TURBINE_CREATE,
					dispatch,
					routeNames.TURBINE_CREATE,
					undefined,
					<PlusCircleOutlined />,
				),
			],
			<ThunderboltOutlined />,
		),
		getItem(
			intl.formatMessage({ id: "nav_group_locations" }),
			routeNames.LOCATIONS,
			dispatch,
			undefined,
			[
				getItem(
					intl.formatMessage({ id: "menu_side_locations" }),
					routeNames.LOCATION_LIST,
					dispatch,
					routeNames.LOCATION_LIST,
					undefined,
					<UnorderedListOutlined />,
				),
				getItem(
					intl.formatMessage({ id: "menu_side_create_location" }),
					routeNames.LOCATION_CREATE,
					dispatch,
					routeNames.LOCATION_CREATE,
					undefined,
					<PlusCircleOutlined />,
				),
			],
			<EnvironmentOutlined />,
		),
		getItem(
			<Flex align="center" justify="space-between" gap={8}>
				<span>{intl.formatMessage({ id: "nav_notifications" })}</span>
				<Badge count={unreadNotificationCount} size="small" />
			</Flex>,
			routeNames.NOTIFICATIONS,
			dispatch,
			routeNames.NOTIFICATIONS,
			undefined,
			<BellOutlined />,
		),
		{ type: "divider" } as ItemType,
		getItem(
			intl.formatMessage({ id: "nav_group_settings" }),
			routeNames.SETTINGS,
			dispatch,
			undefined,
			[
				getItem(
					intl.formatMessage({ id: "menu_side_users" }),
					routeNames.SETTINGS_USER_LIST,
					dispatch,
					routeNames.SETTINGS_USER_LIST,
					undefined,
					<TeamOutlined />,
				),
				getItem(
					intl.formatMessage({ id: "menu_side_materials" }),
					routeNames.SETTINGS_MATERIAL_LIST,
					dispatch,
					routeNames.SETTINGS_MATERIAL_LIST,
					undefined,
					<InboxOutlined />,
				),
				getItem(
					intl.formatMessage({ id: "menu_side_services" }),
					routeNames.SETTINGS_SERVICE_LIST,
					dispatch,
					routeNames.SETTINGS_SERVICE_LIST,
					undefined,
					<ToolOutlined />,
				),
			],
			<SettingOutlined />,
		),
	];
}

type AppProps = {
	dispatch: Dispatch;
	route: RouteState | null;
	intl: IntlShape;
	user: UserState;
};

function collectOpenItems(
	menuItems: ItemType[],
	currentRoute: State,
): string[] {
	const collectedKeys = menuItems.reduce((collected, menuItem) => {
		if (!menuItem?.key) {
			console.error("Invalid menu item: ", menuItem, menuItem?.key);
			return collected;
		}

		const collectedSubKeys =
			"children" in menuItem && menuItem.children
				? collectOpenItems(menuItem.children, currentRoute)
				: ([] as string[]);

		const isSubRoute = currentRoute.name.startsWith(menuItem.key + "");
		const childMenuOpen = collectedSubKeys.length > 0;
		const selfKey = childMenuOpen || isSubRoute ? [menuItem.key + ""] : [];

		return collected.concat(collectedSubKeys, selfKey);
	}, [] as string[]);
	return collectedKeys;
}

function getSelectedKeys(route: State, openItems: string[]): string[] {
	if (route.name === routeNames.TICKET_LIST) {
		const status = route.params?.status;
		return [!status ? TICKET_ALL_KEY : ticketStatusKey(status)];
	}
	if (route.name === routeNames.PROJECT_LIST) {
		const status = route.params?.project_status;
		return [!status ? PROJECT_ALL_KEY : projectStatusKey(status)];
	}
	return openItems;
}

const separator = <Typography.Text>›</Typography.Text>;

const App: React.FC<AppProps & React.PropsWithChildren> = (props) => {
	const { dispatch, children, route, intl, user } = props;
	const {
		token: { colorBgContainer, borderRadiusLG },
	} = theme.useToken();

	// The sidebar badge reads the same hook and adapter instance as the header
	// bell, so both share the module-level cache and can never disagree.
	const { unreadCount } = useNotifications(managementNotificationsAdapter);

	const [menuItems, openItems] = useMemo(() => {
		const items = getMenuItems(intl, dispatch, unreadCount);
		return [items, route ? collectOpenItems(items, route) : []];
	}, [intl, dispatch, route, unreadCount]);

	const selectedKeys = useMemo(
		() => (route ? getSelectedKeys(route, openItems) : []),
		[route, openItems],
	);

	const subheader = {
		title: intl.formatMessage({
			id: "breadcrumb_" + route?.name.replace(/\./g, "_"),
		}),
		breadCrumbItems: route?.name.split(".").map((segment, index, parts) => {
			const subRouteParts = parts.slice(0, index + 1);
			return {
				title: (
					<Typography.Text>
						{intl.formatMessage({
							id: "breadcrumb_" + subRouteParts.join("_"),
						})}
					</Typography.Text>
				),
				pathname: subRouteParts.join("."),
				routeParameters: route.params,
			};
		}),
	};

	if (!(route && user.user)) {
		return null;
	}

	const userDisplayName = getUserDisplayName(user.user);
	const initials = getUserInitials(user.user);

  const createMenuItems: MenuProps["items"] = [
    {
      key: "ticket",
      icon: <FileProtectOutlined />,
      label: intl.formatMessage({ id: "menu_side_create_ticket" }),
      onClick: () => dispatch(actions.navigateTo(routeNames.TICKET_CREATE)),
    },
    {
      key: "project",
      icon: <FolderOutlined />,
      label: intl.formatMessage({ id: "menu_side_create_project" }),
      onClick: () => dispatch(actions.navigateTo(routeNames.PROJECT_CREATE)),
    },
    {
      key: "client",
      icon: <BankOutlined />,
      label: intl.formatMessage({ id: "menu_side_create_client" }),
      onClick: () => dispatch(actions.navigateTo(routeNames.CLIENT_CREATE)),
    },
    {
      key: "turbine",
      icon: <ThunderboltOutlined />,
      label: intl.formatMessage({ id: "menu_side_create_turbines" }),
      onClick: () => dispatch(actions.navigateTo(routeNames.TURBINE_CREATE)),
    },
    {
      key: "location",
      icon: <EnvironmentOutlined />,
      label: intl.formatMessage({ id: "menu_side_create_location" }),
      onClick: () => dispatch(actions.navigateTo(routeNames.LOCATION_CREATE)),
    },
  ];

  const userMenuItems: MenuProps["items"] = [
    {
      key: "user-info",
      type: "group",
      label: (
        <Flex vertical gap={4} style={{ padding: "4px 0" }}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            {userDisplayName}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {user.user?.email}
          </Typography.Text>
          {user.user?.is_staff && (
            <Tag color="blue" style={{ margin: 0, width: "fit-content" }}>
              {intl.formatMessage({ id: "profile_staff_badge" })}
            </Tag>
          )}
        </Flex>
      ),
    },
    { type: "divider" },
    {
      key: "profile",
      icon: <UserOutlined />,
      label: intl.formatMessage({ id: "menu_header_current_user" }),
      onClick: () =>
        dispatch(actions.navigateTo(routeNames.USER_PROFILE_VIEW, { id: user.user?.id })),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: "menu_header_logout" }),
      danger: true,
      onClick: () => dispatch(actions.navigateTo(routeNames.USER_LOGOUT)),
    },
  ];

	return (
		<Layout style={{ minHeight: "100vh" }}>
			<Sider
				width={260}
				breakpoint="lg"
				collapsedWidth={0}
				style={{ background: colorBgContainer }}
			>
				<Menu
					theme="dark"
					mode="inline"
					defaultOpenKeys={openItems}
					selectedKeys={selectedKeys}
					style={{ height: "100%", borderInlineEnd: 0 }}
					items={menuItems}
				/>
			</Sider>
			<Layout>
				<Header style={{ padding: 0, background: colorBgContainer }}>
					<Flex
						align="center"
						justify="space-between"
						style={{ height: "100%", padding: "0 24px 0 24px" }}
					>
						<div>
							<AppBreadCrumbs intl={intl} dispatch={dispatch} route={route} />
						</div>
						<div className={styles.topbarRight}>
							<Flex align="center" gap={8}>
								<Dropdown
									menu={{ items: createMenuItems }}
									placement="bottomRight"
									trigger={["click"]}
								>
									<Button type="primary" icon={<PlusOutlined />}>
										{intl.formatMessage({ id: "menu_header_create" })}
										<DownOutlined />
									</Button>
								</Dropdown>
								<NotificationBell />
								<Dropdown
									menu={{ items: userMenuItems }}
									placement="bottomRight"
									trigger={["click"]}
								>
									<button type="button" className={styles.userMenuTrigger}>
										<Avatar className={styles.topbarAvatar} size={32}>
											{initials}
										</Avatar>
										<span className={styles.topbarUsername}>
											{userDisplayName}
										</span>
									</button>
								</Dropdown>
							</Flex>
						</div>
					</Flex>
				</Header>
				<Layout className={styles.contentWrapper}>
					<Content>{children}</Content>
					<Footer className={styles.footer}>
						<Typography.Text type="secondary">
							{intl.formatMessage(
								{ id: "footer_copyright" },
								{ year: new Date().getFullYear() },
							)}
						</Typography.Text>
					</Footer>
				</Layout>
			</Layout>
		</Layout>
	);
};

export const ConnectedAppLayout = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
	ticket: state.ticket.ticket,
	route: state.router.route,
	token: state.token.token,
	user: state.userstate,
}))(App);

export default ConnectedAppLayout;
